/**
 * Punch List - the remediation work arising from inspections.
 *
 * Every punch-list item across the organisation, oldest first. Until this
 * page existed the items were only reachable one inspection at a time: the
 * dashboard could say how many were open and which contractors were worst,
 * and each inspection showed its own, but nothing listed the items
 * themselves, so there was no surface on which to work through them.
 *
 * It is a queue, not a report, and the ordering says so - longest
 * outstanding leads. An open item blocks its claim from settling, so this is
 * the list of what is holding up money.
 *
 * Two exits per item, both closing it from here rather than making the user
 * open the inspection: RESOLVE (it was fixed) and WAIVE (it will not be, and
 * that is agreed - which needs a reason, because a waiver with no stated
 * reason is indistinguishable from a mistake a year later).
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ClipboardCheck, Wrench } from 'lucide-react'
import { AppShell } from '@/admin/components/app-shell'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  ProductFilterMenu,
  type ProductFilterFacet,
} from '@/shared/components/ui/product-filter-menu'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { toastApiError } from '@/shared/lib/api-error'
import { useTableState } from '@/admin/hooks/use-table-state'
import { useTableFilterPreferences } from '@/admin/hooks/use-table-filter-preferences'
import {
  usePunchListWorkList,
  useClosePunchListWorkItem,
  type PunchListWorkItem,
} from '@/admin/hooks/use-inspections'
import { ClosePunchListItemDialog } from './close-punch-list-item-dialog'

const TABLE_ID = 'inspection-punch-list'

/** Age buckets mirror the repair-tracking dashboard's, so the two agree. */
const AGE_OPTIONS = [
  { value: '0_30_days', labelKey: 'inspections.punchList.age030' },
  { value: '31_60_days', labelKey: 'inspections.punchList.age3160' },
  { value: '61_90_days', labelKey: 'inspections.punchList.age6190' },
  { value: 'over_90_days', labelKey: 'inspections.punchList.ageOver90' },
]

const STATUS_OPTIONS = [
  { value: 'open', labelKey: 'inspections.punchList.statusOpen' },
  { value: 'resolved', labelKey: 'inspections.punchList.statusResolved' },
  { value: 'waived', labelKey: 'inspections.punchList.statusWaived' },
  { value: 'all', labelKey: 'inspections.punchList.statusAll' },
]

/** Older items are the ones that have been ignored longest, so escalate. */
function ageTone(days: number): 'destructive' | 'secondary' | 'outline' {
  if (days > 90) return 'destructive'
  if (days > 30) return 'secondary'
  return 'outline'
}

export function PunchListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canClose = can('inspections.edit.global') || can('inspections.edit.acl')

  const [status, setStatus] = useState('open')
  const [search, setSearch] = useState('')
  const [closing, setClosing] = useState<PunchListWorkItem | null>(null)

  // The platform's standard page: search, the Filters panel and paging run on
  // the server, so every item is reachable however many there are.
  const { page, pageSize, setPage, setPageSize } = useTableState(TABLE_ID)
  const [filterValues, setFilterValues] = useTableFilterPreferences(TABLE_ID)
  const debouncedSearch = useDebouncedValue(search)
  const { data, isPending, isFetching } = usePunchListWorkList({
    status,
    search: debouncedSearch.trim() || undefined,
    // Filters panel: each facet's values comma-joined (the server ORs them).
    contractor: filterValues.contractor?.join(',') || undefined,
    category: filterValues.category?.join(',') || undefined,
    age: filterValues.age?.join(',') || undefined,
    page,
    page_size: pageSize,
  })
  const items = data?.results ?? []
  const close = useClosePunchListWorkItem()

  // Contractors and categories come from the server (scoped by status only,
  // so a pick never shrinks its own list); the age buckets are fixed.
  const facets: ProductFilterFacet[] = useMemo(
    () => [
      {
        key: 'age',
        label: t('inspections.punchList.colAge', 'Age'),
        type: 'select',
        kind: 'field',
        options: AGE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
      },
      ...(data?.filter_options ?? []),
    ],
    [t, data?.filter_options],
  )

  const columns: DataTableColumn<PunchListWorkItem>[] = [
    {
      id: 'description',
      header: t('inspections.punchList.colItem', 'Item'),
      accessorKey: 'description',
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-[var(--ink)]">{row.description}</span>
          {row.category && (
            <span className="text-[11px] text-[var(--ink-3)]">
              {row.category}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'inspection_code',
      header: t('inspections.punchList.colInspection', 'Inspection'),
      accessorKey: 'inspection_code',
      cell: (row) => (
        <Button
          variant="link"
          size="xs"
          className="h-auto p-0 text-[var(--ink-2)]"
          onClick={(e) => {
            e.stopPropagation()
            navigate(`/inspections/${row.inspection_uuid}`)
          }}
        >
          {row.inspection_code}
        </Button>
      ),
    },
    {
      id: 'contractor_name',
      header: t('inspections.punchList.colContractor', 'Contractor'),
      accessorKey: 'contractor_name',
      cell: (row) => row.contractor_name || '-',
    },
    {
      id: 'property_address',
      header: t('inspections.punchList.colProperty', 'Property'),
      accessorKey: 'property_address',
      cell: (row) => row.property_address || '-',
    },
    {
      id: 'age_days',
      header: t('inspections.punchList.colAge', 'Age'),
      accessorKey: 'age_days',
      cell: (row) => (
        <Badge variant={ageTone(row.age_days)}>
          {t('inspections.punchList.days', '{{count}} days', {
            count: row.age_days,
          })}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: t('inspections.punchList.colStatus', 'Status'),
      accessorKey: 'status',
      cell: (row) =>
        row.status === 'open' ? (
          canClose ? (
            <Button
              size="xs"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                setClosing(row)
              }}
            >
              {t('inspections.punchList.close', 'Close')}
            </Button>
          ) : (
            <Badge variant="outline">{row.status_label}</Badge>
          )
        ) : (
          <Badge variant="secondary">{row.status_label}</Badge>
        ),
    },
  ]

  async function submitClose(action: 'resolve' | 'waive', text: string, photos: string[]) {
    if (!closing) return
    try {
      await close.mutateAsync({
        inspectionUuid: closing.inspection_uuid,
        itemUuid: closing.uuid,
        action,
        notes: action === 'resolve' ? text : undefined,
        photos: action === 'resolve' ? photos : undefined,
        reason: action === 'waive' ? text : undefined,
      })
      toast.success(
        action === 'resolve'
          ? t('inspections.punchList.resolved', 'Item resolved.')
          : t('inspections.punchList.waived', 'Item waived.'),
      )
      setClosing(null)
    } catch (e) {
      toastApiError(e, t('inspections.punchList.closeFailed', 'Could not close that item.'))
    }
  }

  return (
    <AppShell>
      <main className="mx-auto w-full max-w-[1400px] px-6 py-8">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-[34px] italic text-[var(--ink)]">
              {t('inspections.punchList.title', 'Punch List')}
            </h1>
            <p className="mt-1 text-[13px] text-[var(--ink-2)]">
              {t(
                'inspections.punchList.subtitle',
                'Everything inspections have flagged for remediation, longest outstanding first. An open item holds up its claim.',
              )}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/inspections/dashboard')}
            className="rounded-[4px] px-3 text-[11px] font-normal uppercase tracking-[0.1em]"
          >
            <ClipboardCheck size={13} className="mr-1.5" />
            {t('inspections.punchList.dashboard', 'Dashboard')}
          </Button>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <SearchInput
            value={search}
            onChange={(v) => {
              setSearch(v)
              setPage(1)
            }}
            placeholder={t(
              'inspections.punchList.searchPlaceholder',
              'Search item, inspection, contractor, or property…',
            )}
            className="w-[320px]"
          />
          <Select
            value={status}
            onValueChange={(v: string) => {
              setStatus(v)
              setPage(1)
            }}
          >
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ProductFilterMenu
            facets={facets}
            value={filterValues}
            onChange={(values) => {
              setFilterValues(values)
              setPage(1)
            }}
          />
        </div>

        <DataTable<PunchListWorkItem>
          tableId="inspection-punch-list"
          columns={columns}
          data={items}
          totalCount={data?.count ?? 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          rowKey={(row) => row.uuid}
          onRowClick={(row) => navigate(`/inspections/${row.inspection_uuid}`)}
          // BOTH flags: a cached-empty result refetching in the background
          // would otherwise flash the empty state over work that exists.
          isLoading={isPending}
          isFetching={isFetching}
          emptyStateNode={
            <EmptyState
              icon={Wrench}
              title={t('inspections.punchList.emptyTitle', 'Nothing outstanding')}
              description={t(
                'inspections.punchList.emptyBody',
                'No punch-list items match this filter. Items appear here when an inspection flags work that needs remedying.',
              )}
              testId="punch-list-empty"
            />
          }
        />
      </main>

      <ClosePunchListItemDialog
        item={closing}
        inspectionUuid={closing?.inspection_uuid}
        open={closing !== null}
        onOpenChange={(next) => !next && setClosing(null)}
        onSubmit={submitClose}
        isPending={close.isPending}
      />
    </AppShell>
  )
}
